import { getStatusIconUrl } from '../../components/ItemIcon';
import { displayMomentum } from '../../../utils/format';
import { getConfig } from './types';

type EffectKind = 'buff' | 'debuff' | 'dot' | 'amplifier' | 'control';

const AMPLIFIER_IDS = new Set(['fractured', 'exposed', 'marked', 'vulnerable']);
const CONTROL_IDS = new Set(['stunned', 'paralyzed', 'frozen', 'rooted']);

function classifyEffect(e: { id: string; category: string; dotDamage?: number; momentumPenalty?: number }): EffectKind {
  if (AMPLIFIER_IDS.has(e.id)) return 'amplifier';
  if ((e.dotDamage ?? 0) > 0) return 'dot';
  if (e.momentumPenalty || CONTROL_IDS.has(e.id)) return 'control';
  return e.category as 'buff' | 'debuff';
}

const KIND_LABELS: Record<EffectKind | 'all', string> = {
  all: 'All',
  buff: 'Buff',
  debuff: 'Debuff',
  dot: 'DoT',
  amplifier: 'Amplifier',
  control: 'Control',
};

export interface EffectsState {
  kindFilter: EffectKind | '';
}

export function createEffectsState(): EffectsState {
  return { kindFilter: '' };
}

export function renderEffects(content: HTMLElement, state: EffectsState): void {
  const config = getConfig();
  if (!config) { content.innerHTML = '<p>Loading...</p>'; return; }

  // Filter out enemy-only effects — players don't need to see "Hardened", "Shadowflame", etc.
  const playerEffects = Object.values(config.statusEffects).filter(e => e.source !== 'enemy');

  const subcategoryLabel = (e: { category: string; subcategory?: string }): string | null => {
    const sub = e.subcategory ?? (e.category === 'buff' ? 'boon' : null);
    if (sub === 'hex' || sub === 'ruin' || sub === 'boon' || sub === 'barrier' || sub === 'regen') {
      return sub.charAt(0).toUpperCase() + sub.slice(1);
    }
    return null;
  };

  const classified = playerEffects.map(e => ({ effect: e, kind: classifyEffect(e) }));
  const filtered = state.kindFilter
    ? classified.filter(x => x.kind === state.kindFilter)
    : classified;

  // Sort: kind first (buff, debuff, dot, amplifier, control), then alpha
  const kindOrder: EffectKind[] = ['buff', 'debuff', 'dot', 'amplifier', 'control'];
  filtered.sort((a, b) => {
    const ai = kindOrder.indexOf(a.kind);
    const bi = kindOrder.indexOf(b.kind);
    if (ai !== bi) return ai - bi;
    return a.effect.name.localeCompare(b.effect.name);
  });

  // Build "applied by" map: effect ID → player ability names (filter out enemy abilities)
  const appliedBy: Record<string, string[]> = {};
  for (const ab of Object.values(config.abilities)) {
    if (!(ab.tags ?? []).includes('player')) continue;
    if (ab.applyTargetEffect) {
      if (!appliedBy[ab.applyTargetEffect]) appliedBy[ab.applyTargetEffect] = [];
      appliedBy[ab.applyTargetEffect].push(ab.name);
    }
    if (ab.applySelfEffect) {
      if (!appliedBy[ab.applySelfEffect]) appliedBy[ab.applySelfEffect] = [];
      appliedBy[ab.applySelfEffect].push(ab.name + ' (self)');
    }
  }

  // Counts per kind for pill labels
  const counts: Record<string, number> = { all: classified.length };
  for (const k of kindOrder) counts[k] = classified.filter(x => x.kind === k).length;

  const pillRow = (['all', ...kindOrder] as const).filter(k => k === 'all' || counts[k] > 0).map(k =>
    `<button class="codex-pill${(state.kindFilter === '' && k === 'all') || state.kindFilter === k ? ' active' : ''}" data-effect-kind="${k}">${KIND_LABELS[k]} <span class="codex-pill-count">${counts[k]}</span></button>`
  ).join('');

  const effectCards = filtered.map(({ effect: e, kind }) => {
    const cls = e.category === 'buff' ? 'buff' : 'debuff';
    const duration = e.defaultTurns > 0 && e.defaultTurns < 99 ? `${e.defaultTurns} turns` : 'Permanent';
    const sources = appliedBy[e.id] ?? [];
    const sourcesHtml = sources.length > 0
      ? `<div class="codex-effect-sources">
           <span class="codex-effect-sources-label">Applied by</span>
           ${sources.map(s => `<span class="codex-effect-source">${s}</span>`).join('')}
         </div>`
      : '';

    const iconSrc = getStatusIconUrl(e.id);
    const iconHtml = `<img class="codex-effect-icon" src="${iconSrc}" alt="" />`;

    let mechanicHtml = '';
    if (e.dotDamage) mechanicHtml += `<div class="codex-effect-mechanic">Deals ${e.dotDamage} damage per turn</div>`;
    if (e.multiplier && e.multiplier !== 1) {
      const pct = Math.round((e.multiplier - 1) * 100);
      const sign = pct > 0 ? '+' : '';
      mechanicHtml += `<div class="codex-effect-mechanic">${sign}${pct}% damage modifier</div>`;
    }
    if (e.momentumPenalty) mechanicHtml += `<div class="codex-effect-mechanic">-${displayMomentum(e.momentumPenalty)} Momentum</div>`;

    const kindBadge = `<span class="codex-effect-kind codex-effect-kind--${kind}">${KIND_LABELS[kind]}</span>`;
    const subLabel = subcategoryLabel(e);
    const subBadge = subLabel
      ? `<span class="codex-effect-kind codex-effect-kind--${subLabel.toLowerCase()}">${subLabel}</span>`
      : '';

    return `
      <div class="codex-effect-card">
        <div class="codex-effect-header">
          ${iconHtml}
          <div class="codex-effect-header-text">
            <span class="codex-skill-name ${cls}">${e.name}</span>
            <span class="codex-effect-meta">${kindBadge}${subBadge}<span class="codex-effect-duration">${duration}</span></span>
          </div>
        </div>
        ${e.description ? `<div class="codex-mastery-desc">${e.description}</div>` : ''}
        ${mechanicHtml}
        ${sourcesHtml}
      </div>
    `;
  }).join('');

  content.innerHTML = `
    <h2 class="codex-section-title">Status Effects</h2>
    <p class="codex-section-subtitle">${playerEffects.length} player effects</p>
    <div class="codex-filter-pills">${pillRow}</div>
    <div class="codex-mastery-grid">${effectCards}</div>
  `;

  content.querySelectorAll('[data-effect-kind]').forEach(btn => {
    btn.addEventListener('click', () => {
      const k = (btn as HTMLElement).dataset.effectKind!;
      state.kindFilter = (k === 'all') ? '' : (k as EffectKind);
      renderEffects(content, state);
    });
  });
}

